import { useNavigate, Link } from "react-router-dom";
import { Shield, User, Menu, X, Archive, ShoppingCart, Sparkles, LogOut, Package, LayoutDashboard, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { useState, useEffect } from "react";
import AnimatedLogo from "./AnimatedLogo";
import ProductFinderChat from "./ProductFinderChat";
import { API } from "../App";
import { authFetch, clearSessionToken } from "../utils/auth";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { toast } from "sonner";

export default function Header({ showAuth = true }) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Optimistic initial state: if a session token exists in localStorage, assume
  // logged-in on first paint (avoids "Sign In -> My Vault" flash for logged-in users).
  // The token is validated in the effect below and cleared if the backend rejects it.
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem("session_token"));
  const [currentUser, setCurrentUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [showProductFinder, setShowProductFinder] = useState(false);

  useEffect(() => {
    // Validate the token against the backend so a stale/expired token
    // does not keep "My Vault" (and other authed nav) visible after sign-out or expiry.
    let cancelled = false;
    const validateSession = async () => {
      const token = localStorage.getItem("session_token");
      if (!token) {
        setIsLoggedIn(false);
        setCurrentUser(null);
        return;
      }
      try {
        const res = await authFetch(`${API}/auth/me`);
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setIsLoggedIn(true);
          setCurrentUser(data);
        } else if (res.status === 401 || res.status === 403) {
          // Definitive auth failure — clear the stale token.
          clearSessionToken();
          setIsLoggedIn(false);
          setCurrentUser(null);
        }
        // Any other status (5xx / network hiccup) → leave state as-is (optimistic).
      } catch {
        // Network error / aborted fetch — do NOT clear the token, stay logged-in optimistically.
      }
    };
    validateSession();

    // Get cart count
    const updateCartCount = () => {
      try {
        const cart = JSON.parse(localStorage.getItem('happyages_cart') || '[]');
        setCartCount(cart.reduce((sum, item) => sum + (item.quantity || 1), 0));
      } catch (e) {
        setCartCount(0);
      }
    };
    
    updateCartCount();
    
    // Listen for storage changes
    window.addEventListener('storage', updateCartCount);
    
    return () => {
      cancelled = true;
      window.removeEventListener('storage', updateCartCount);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, { method: "POST" });
    } catch {
      // ignore — we still clear locally
    }
    clearSessionToken();
    setIsLoggedIn(false);
    setCurrentUser(null);
    toast.success("Signed out");
    navigate("/");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 sm:gap-3">
          <AnimatedLogo className="w-6 h-6 sm:w-8 sm:h-8" />
          <span className="font-cinzel text-sm sm:text-xl font-bold text-[#D4AF37] tracking-widest whitespace-nowrap">HAPPY AGES</span>
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden sm:flex items-center gap-4">
          <Button
            variant="ghost"
            className="text-[#F8FAFC] hover:text-[#D4AF37] font-manrope"
            onClick={() => navigate("/showcase")}
          >
            Showcase
          </Button>
          {isLoggedIn && (
            <Button
              variant="ghost"
              className="text-[#F8FAFC] hover:text-[#D4AF37] font-manrope"
              onClick={() => navigate("/gallery")}
              data-testid="nav-gallery-btn"
            >
              Gallery
            </Button>
          )}
          <Button
            variant="ghost"
            className="text-[#F8FAFC] hover:text-[#D4AF37] font-manrope"
            onClick={() => navigate("/blog")}
          >
            Blog
          </Button>
          <Button 
            variant="ghost" 
            className="text-[#F8FAFC] hover:text-[#D4AF37] font-manrope relative"
            onClick={() => navigate("/cart")}
            data-testid="cart-header-btn"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#D4AF37] text-[#0F172A] text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {cartCount > 9 ? '9+' : cartCount}
              </span>
            )}
          </Button>
          {isLoggedIn && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="text-[#D4AF37] hover:text-[#F8FAFC] font-manrope gap-1"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-7 h-7 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37]">
                    {(currentUser?.name || currentUser?.email || "U").charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden md:inline max-w-[140px] truncate">
                    {currentUser?.name?.split(" ")[0] || "Account"}
                  </span>
                  <ChevronDown className="w-4 h-4 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56 bg-[#0F172A] border border-[#D4AF37]/30 text-[#F8FAFC]"
                data-testid="user-menu-content"
              >
                <DropdownMenuLabel className="font-cinzel text-[#D4AF37]">
                  <div className="truncate">{currentUser?.name || "My Account"}</div>
                  <div className="text-xs text-[#94A3B8] font-manrope truncate mt-0.5 font-normal">
                    {currentUser?.email}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#D4AF37]/20" />
                <DropdownMenuItem
                  onClick={() => navigate("/vault")}
                  className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#F8FAFC]"
                  data-testid="user-menu-vault"
                >
                  <Archive className="w-4 h-4 mr-2 text-[#D4AF37]" />
                  My Vault
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/vault?tab=orders")}
                  className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#F8FAFC]"
                  data-testid="user-menu-orders"
                >
                  <Package className="w-4 h-4 mr-2 text-[#D4AF37]" />
                  My Orders
                </DropdownMenuItem>
                {currentUser?.role === "admin" ? (
                  <DropdownMenuItem
                    onClick={() => navigate("/admin")}
                    className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#F8FAFC]"
                    data-testid="user-menu-admin"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    Admin Dashboard
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => navigate("/dashboard")}
                    className="cursor-pointer focus:bg-[#D4AF37]/10 focus:text-[#F8FAFC]"
                    data-testid="user-menu-gallery"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    My Gallery
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-[#D4AF37]/20" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer focus:bg-red-500/10 focus:text-red-400 text-[#F8FAFC]"
                  data-testid="user-menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {showAuth && (
            <>
              {!isLoggedIn && (
                <Button 
                  variant="ghost" 
                  className="text-[#F8FAFC] hover:text-[#D4AF37] font-manrope"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </Button>
              )}
              <Button 
                className="bg-[#D4AF37] text-[#020617] hover:bg-[#B5952F] font-cinzel tracking-wider"
                onClick={() => setShowProductFinder(true)}
                data-testid="get-started-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Speak to Happy
              </Button>
            </>
          )}
        </div>

        {/* Mobile Navigation */}
        <div className="flex sm:hidden items-center gap-2">
          {showAuth && (
            <Button 
              variant="ghost" 
              size="icon"
              className="text-[#F8FAFC] hover:text-[#D4AF37]"
              onClick={() => navigate("/login")}
            >
              <User className="w-5 h-5" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon"
            className="text-[#F8FAFC] hover:text-[#D4AF37]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="sm:hidden bg-[#0F172A]/95 border-t border-[#D4AF37]/20 backdrop-blur-lg">
          <nav className="flex flex-col px-4 py-4 space-y-1">
            <Button
              variant="ghost"
              className="justify-start text-[#F8FAFC] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 font-manrope"
              onClick={() => { navigate("/showcase"); setMobileMenuOpen(false); }}
            >
              Showcase
            </Button>
            {isLoggedIn && (
              <Button
                variant="ghost"
                className="justify-start text-[#F8FAFC] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 font-manrope"
                onClick={() => { navigate("/gallery"); setMobileMenuOpen(false); }}
                data-testid="mobile-nav-gallery-btn"
              >
                Gallery
              </Button>
            )}
            <Button
              variant="ghost"
              className="justify-start text-[#F8FAFC] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 font-manrope"
              onClick={() => { navigate("/blog"); setMobileMenuOpen(false); }}
            >
              Blog
            </Button>
            {!isLoggedIn && (
              <Button 
                variant="ghost" 
                className="justify-start text-[#F8FAFC] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 font-manrope"
                onClick={() => { navigate("/login"); setMobileMenuOpen(false); }}
              >
                Sign In
              </Button>
            )}
            {isLoggedIn && (
              <>
                <div className="pt-2 border-t border-[#334155] mt-2">
                  <div className="px-3 py-2 text-xs text-[#94A3B8] truncate">
                    {currentUser?.email}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  className="justify-start text-[#D4AF37] hover:text-[#F8FAFC] hover:bg-[#D4AF37]/10 font-manrope"
                  onClick={() => { navigate("/vault"); setMobileMenuOpen(false); }}
                  data-testid="mobile-menu-vault"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  My Vault
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-[#D4AF37] hover:text-[#F8FAFC] hover:bg-[#D4AF37]/10 font-manrope"
                  onClick={() => { navigate("/vault?tab=orders"); setMobileMenuOpen(false); }}
                  data-testid="mobile-menu-orders"
                >
                  <Package className="w-4 h-4 mr-2" />
                  My Orders
                </Button>
                {currentUser?.role === "admin" ? (
                  <Button
                    variant="ghost"
                    className="justify-start text-[#D4AF37] hover:text-[#F8FAFC] hover:bg-[#D4AF37]/10 font-manrope"
                    onClick={() => { navigate("/admin"); setMobileMenuOpen(false); }}
                    data-testid="mobile-menu-admin"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Admin Dashboard
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    className="justify-start text-[#D4AF37] hover:text-[#F8FAFC] hover:bg-[#D4AF37]/10 font-manrope"
                    onClick={() => { navigate("/dashboard"); setMobileMenuOpen(false); }}
                    data-testid="mobile-menu-gallery"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    My Gallery
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="justify-start text-[#F8FAFC] hover:text-red-400 hover:bg-red-500/10 font-manrope"
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  data-testid="mobile-menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </>
            )}
            <div className="pt-2 border-t border-[#334155]">
              <Button 
                className="w-full bg-[#D4AF37] text-[#020617] hover:bg-[#B5952F] font-cinzel tracking-wider"
                onClick={() => { setShowProductFinder(true); setMobileMenuOpen(false); }}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Speak to Happy
              </Button>
            </div>
          </nav>
        </div>
      )}

      {/* Product Finder Chat - Uses portal to render to body */}
      <ProductFinderChat 
        isOpen={showProductFinder} 
        onClose={() => setShowProductFinder(false)} 
      />
    </header>
  );
}
